import React, { createContext, useContext, useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { isMasterEmail } from '../constants';
import { WifiOff } from 'lucide-react';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  session: Session | null;
  status: AuthStatus;
  isMasterUser: boolean;
  isPasswordRecovery: boolean;
  setIsPasswordRecovery: (value: boolean) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  status: 'loading',
  isMasterUser: false,
  isPasswordRecovery: false,
  setIsPasswordRecovery: () => {},
  signOut: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

// Auxiliar para verificar se a URL possui fragmentos/parâmetros de Callback do Supabase (Magic Link, OAuth, PKCE)
function hasAuthCallbackParamsInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  return (
    hash.includes('access_token=') ||
    hash.includes('refresh_token=') ||
    hash.includes('type=magiclink') ||
    hash.includes('type=recovery') ||
    search.includes('code=') ||
    search.includes('error=')
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [connectionError, setConnectionError] = useState<boolean>(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.location.hash.includes('type=recovery') ||
      window.location.search.includes('type=recovery') ||
      window.location.search.includes('mode=reset-password')
    );
  });

  useEffect(() => {
    // Limpa a flag de recarregamento de chunk ao iniciar com sucesso
    sessionStorage.removeItem('rmax_chunk_reload_attempted');

    const isCallback = hasAuthCallbackParamsInUrl();

    // Safety timeout: Não desloga o usuário em caso de latência móvel.
    // Se após 12s ainda estiver em loading e sem sessão, ativa estado de conexão degradada com opção de retry.
    const timeout = setTimeout(() => {
      if (isMounted) {
        setStatus(prev => {
          if (prev === 'loading') {
            setConnectionError(true);
          }
          return prev;
        });
      }
    }, isCallback ? 15000 : 12000);

    let isMounted = true;

    // 1. Registrar ouvinte onAuthStateChange PRIMEIRO para capturar o evento do Magic Link
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      if (!isMounted) return;

      if (import.meta.env.DEV) {
        console.log(`R-MAX Auth Event: ${event}`, s?.user ? `[User: ${s.user.email}]` : '[Sem sessão]');
      }

      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }

      if (s?.user) {
        setSession(s);
        setStatus('authenticated');
        setConnectionError(false);
        clearTimeout(timeout);
        localStorage.removeItem('rmax_pending_magic_email');
        localStorage.removeItem('rmax_magic_sent_at');

        // Se veio por Magic Link / Callback e possui hash/query auth, limpa a URL de forma suave
        if (hasAuthCallbackParamsInUrl()) {
          const isRecovery =
            event === 'PASSWORD_RECOVERY' ||
            window.location.hash.includes('type=recovery') ||
            window.location.search.includes('type=recovery') ||
            window.location.search.includes('mode=reset-password');

          if (isRecovery) {
            setIsPasswordRecovery(true);
            const cleanUrl = `${window.location.origin}/auth?mode=reset-password`;
            window.history.replaceState(window.history.state, '', cleanUrl);
          } else {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState(window.history.state, '', cleanUrl);
          }
        }
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setStatus('unauthenticated');
        setConnectionError(false);
        clearTimeout(timeout);
      } else if (!isCallback && event !== 'USER_UPDATED') {
        setSession(null);
        setStatus('unauthenticated');
        setConnectionError(false);
        clearTimeout(timeout);
      }
    });

    // 2. Consulta de sessão inicial usando cache local do SDK
    supabase.auth
      .getSession()
      .then(({ data: { session: s } }) => {
        if (!isMounted) return;
        if (s?.user) {
          setSession(s);
          setStatus('authenticated');
          setConnectionError(false);
          clearTimeout(timeout);
        } else if (!isCallback) {
          // Apenas define 'unauthenticated' se NÃO houver parâmetros de callback na URL.
          setSession(null);
          setStatus('unauthenticated');
          setConnectionError(false);
          clearTimeout(timeout);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        const msg = (err?.message || '').toLowerCase();
        const isNetworkError =
          msg.includes('fetch') ||
          msg.includes('network') ||
          msg.includes('timeout') ||
          err?.status === 504 ||
          err?.status === 502 ||
          err?.status === 503;

        const isTokenRevoked =
          msg.includes('refresh_token_not_found') ||
          msg.includes('invalid_grant') ||
          msg.includes('invalid refresh token') ||
          err?.status === 400 ||
          err?.status === 401;

        if (isTokenRevoked) {
          setSession(null);
          setStatus('unauthenticated');
          setConnectionError(false);
          clearTimeout(timeout);
        } else if (isNetworkError) {
          // Erro de rede transitório: se já houver sessão em memória/cache, preserva
          setSession(prev => {
            if (prev?.user) {
              setStatus('authenticated');
              return prev;
            }
            setConnectionError(true);
            return null;
          });
        } else if (!isCallback) {
          setSession(null);
          setStatus('unauthenticated');
          setConnectionError(false);
          clearTimeout(timeout);
        }
      });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const isMasterUser = Boolean(
    session?.user?.email && isMasterEmail(session.user.email)
  );

  const signOut = async () => {
    try {
      localStorage.removeItem('rmax_data');
      localStorage.removeItem('rmax_current_cashier_id');
      localStorage.removeItem('rmax_current_cashier_date');
      if (session?.user?.id) {
        localStorage.removeItem(`rmax_current_cashier_id_${session.user.id}`);
        localStorage.removeItem(`rmax_current_cashier_date_${session.user.id}`);
      }
      await supabase.auth.signOut();
      setSession(null);
      setStatus('unauthenticated');
    } catch (error) {
      console.error('Erro ao sair da conta:', error);
    }
  };

  if (connectionError && status === 'loading' && !session) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center font-sans">
        <div className="w-12 h-12 rounded-2xl bg-amber-100 border border-amber-200 text-amber-800 flex items-center justify-center mb-4">
          <WifiOff className="w-6 h-6" />
        </div>
        <h2 className="text-base font-bold text-slate-900 mb-1">Conexão lenta ou instável</h2>
        <p className="text-slate-500 text-xs max-w-xs mb-6">
          Não conseguimos validar sua conexão com o servidor no momento. Verifique sua internet.
        </p>
        <button
          type="button"
          onClick={() => {
            setConnectionError(false);
            window.location.reload();
          }}
          className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer shadow-sm active:scale-95"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ session, status, isMasterUser, isPasswordRecovery, setIsPasswordRecovery, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

